import { Clear } from '@mui/icons-material'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator } from '@mui/lab'
import { Box, Button, CardActions, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography, useMediaQuery } from '@mui/material'
import moment from 'moment'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useLazyGetArchivedOrderZoneHistoryQuery, useLazyGetOrderZoneHistoryQuery } from 'src/_api/orderApi'
import { useLazyGetArchivedTaskZoneHistoryQuery, useLazyGetTaskZoneHistoryQuery } from 'src/_api/taskApi'
import { ROLES } from 'src/constants'

export default function ZoneHistoryDialog({ open, onClose, id, type }) {
    const { t } = useTranslation()

    const { pathname } = useLocation()

    const [searchParams] = useSearchParams()
    const archived = pathname.includes("/archived/") || searchParams.get("tab") === "archived"

    const { profile } = useSelector((state) => state.entities.profile)

    const [getOrderZoneHistory, { data: orderZonesHistory, isLoading: orderLoading }] = useLazyGetOrderZoneHistoryQuery()
    const [getArchivedOrderZoneHistory, { data: archivedOrderZonesHistory, isLoading: archivedOrderLoading }] = useLazyGetArchivedOrderZoneHistoryQuery()
    const [getTaskZoneHistory, { data: taskZonesHistory, isLoading: taskLoading }] = useLazyGetTaskZoneHistoryQuery()
    const [getArchivedTaskZoneHistory, { data: archivedTaskZonesHistory, isLoading: archivedTaskLoading }] = useLazyGetArchivedTaskZoneHistoryQuery()

    const fullScreen = useMediaQuery(theme => theme.breakpoints.down('md'));

    const getZoneHistory = type === "ORDER"
        ? (archived ? getArchivedOrderZoneHistory : getOrderZoneHistory)
        : (archived ? getArchivedTaskZoneHistory : getTaskZoneHistory)

    const data = type === "ORDER"
        ? (archived ? archivedOrderZonesHistory : orderZonesHistory)
        : (archived ? archivedTaskZonesHistory : taskZonesHistory)

    const isLoading = type === "ORDER"
        ? (archived ? archivedOrderLoading : orderLoading)
        : (archived ? archivedTaskLoading : taskLoading)

    useEffect(() => {
        if (!!id && !!type && profile?.role === ROLES.admin) {
            getZoneHistory(id)
        }
    }, [archived, getZoneHistory, id, profile?.role, type])

    return (
        <Dialog
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            open={open}
            aria-labelledby="guard-dialog-title"
            disableScrollLock
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {t('Track Zones History')}
                <IconButton onClick={() => onClose(false)} color="primary">
                    <Clear />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent>
                <Timeline >
                    {(data?.zoneHistory?.length === 0 && !isLoading) && <Typography textAlign="center" fontWeight="bold">{t("No data found.")}</Typography>}
                    {
                        data?.zoneHistory?.map((zone, index) => (
                            <TimelineItem sx={{ mb: 2 }}>
                                <TimelineOppositeContent color="text.secondary">
                                    {moment(zone?.dateTime).format('DD/MM/YY - h:mm A')}
                                </TimelineOppositeContent>
                                <TimelineSeparator>
                                    <TimelineDot />
                                    {data?.zoneHistory?.length - 1 !== index && <TimelineConnector />}
                                </TimelineSeparator>
                                <TimelineContent>
                                    <Box display="flex" alignItems="center">
                                        <Typography fontWeight="bold" sx={{ mr: 1.5 }} color="text.lighter" variant='body2'>
                                            {t("Zone")}
                                        </Typography>
                                        <Typography variant="h6">{zone?.zone?.properties?.name ?? t("Unlocated")}</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Typography fontWeight="bold" sx={{ mr: 1.5 }} color="text.lighter" variant='body2'>
                                            {t("Action Status")}
                                        </Typography>
                                        <Typography>{t(`actionStatus.${zone?.actionStatus}`)}</Typography>
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Typography fontWeight="bold" sx={{ mr: 1.5 }} color="text.lighter" variant='body2'>
                                            {t("Status")}
                                        </Typography>
                                        <Typography>{t(zone?.status)}</Typography>
                                    </Box>
                                </TimelineContent>
                            </TimelineItem>
                        ))
                    }
                </Timeline>
            </DialogContent>
            <Divider />
            <CardActions>
                <Button onClick={() => onClose()} color="primary" variant="contained" sx={{ textTransform: "none" }}>
                    {t('Close')}
                </Button>
            </CardActions>
        </Dialog>
    )
}
